import { useState } from 'react';
import { Minus } from 'lucide-react';
import { useLanguage } from '../../contexts/language/LanguageContext';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useSEO } from '../../hooks/useSEO';
import { INTERSECTION_THRESHOLDS } from '../../helpers/constants.helper';
import './ServicesPage.scss';

const ServicesPage = () => {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [intersectionRef, isVisible] = useIntersectionObserver({
    threshold: INTERSECTION_THRESHOLDS.MEDIUM
  });

  // SEO optimization
  useSEO({
    title: 'Services - ALINA POLONEYEVA',
    description: 'Professional visual content creation and modeling services by Ukrainian creator Alina Poloneyeva. Editorial fashion, brand collaborations, fashion content creation. Based in Warsaw and Paris.',
    keywords: 'modeling services, fashion content creation services, editorial modeling, brand collaboration, fashion week services, Ukrainian model services, Warsaw model, Paris model',
    ogTitle: 'Services - ALINA POLONEYEVA',
    ogDescription: 'Professional visual content creation and modeling services',
    ogImage: 'https://alinapoloneyeva.com/images/about/about.jpg',
    ogUrl: 'https://alinapoloneyeva.com/services',
    canonical: 'https://alinapoloneyeva.com/services',
  });

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleAccordion(index);
    }
  };

  return (
    <section className="services" ref={intersectionRef}>
      <div className={`services__container ${isVisible ? 'visible' : ''}`}>
        <div className="services__header">
          <h2 className="services__title">{t.terms.title}</h2>
          {/* <p className="services__subtitle">{t.terms.subtitle}</p> */}
        </div>

        <div className="services__services">
          {t.terms.services.map((service, index) => (
            <article
              key={index}
              className={`services__service ${openIndex === index ? 'is-expanded' : ''}`}
            >
              <button
                className="services__service-header"
                onClick={() => toggleAccordion(index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                aria-expanded={openIndex === index}
                aria-controls={`service-content-${index}`}
              >
                <div className="services__service-info">
                  <h3 className="services__service-title">{service.title}</h3>
                  <p className="services__service-description">{service.description}</p>
                </div>
                <div className="services__service-indicator">
                  <span className="services__service-number">0{index + 1}</span>
                  <div className="services__service-icon-wrapper">
                    <Minus
                      className="services__service-icon"
                      size={20}
                      strokeWidth={1}
                    />
                  </div>
                </div>
              </button>

              <div
                id={`service-content-${index}`}
                className="services__service-content"
                role="region"
                aria-labelledby={`service-title-${index}`}
              >
                <div className="services__service-content-wrapper">
                  <div className="services__service-content-inner">
                    <div className="services__service-content-text">
                      <p>{service.content}</p>
                      <p className="services__service-benefits">
                        <span className="services__service-benefits-label">{t.terms.benefitsLabel}</span>
                        <span className="services__service-benefits-text">{service.benefits}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesPage;
